import { useContext, useMemo } from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import { type AxisOptions, Chart, type UserSerie } from "react-charts";
import type {
  ChartPeriod,
  ChartPeriodKey,
  PricePoint,
} from "../../../api/src/security-details/market-statistics";
import { ThemeContext } from "../lib/display";
import {
  DataDate,
  DID_NOT_ANSWER,
  type MarketDataQuery,
  NOT_AVAILABLE,
  returnText,
} from "./MarketDataPanels";

// APP-1487 (doc-security-market-data-01a150c533210e70): the chart of the
// Overview tab. Every variant but a bond shows the total return of the
// security and of `SP-DA` (R16, R17, R18, R46); a bond shows its price
// (R114, R115, R118). The route sends the six periods with their disabled
// flags and their prices, so a period button changes the chart with no new
// request. The benchmark is fixed: no control changes it (R19).

/** The periods in the order of their buttons (R16). */
const PERIODS: readonly ChartPeriodKey[] = [
  "1M",
  "6M",
  "YTD",
  "1Y",
  "3Y",
  "5Y",
];

/** The period the chart opens on while it is not disabled (R52). */
const DEFAULT_PERIOD: ChartPeriodKey = "1Y";

/** The name of the benchmark of every variant but a bond (R18). */
export const BENCHMARK_NAME = "S&P 500 (dividend adjusted)";

const COLORS = ["#00B0B0", "#54636d"];

type ChartPoint = { date: Date; value: number };

/** A bond price with two decimals, as the page header shows it (R14). */
const priceText = (value: number) => value.toFixed(2);

type Line = {
  label: string;
  /** The points of the line, or the text in its place (R55). */
  points: ChartPoint[] | string;
};

/**
 * The period the chart opens on: 1Y, or when 1Y is disabled the longest
 * period that is not, the one whose first date is the oldest (R52, R58).
 * Null when every period is disabled.
 */
export function defaultPeriod(
  periods: readonly ChartPeriod[],
): ChartPeriodKey | null {
  const enabled = periods.filter((period) => !period.disabled);
  if (enabled.some((period) => period.period === DEFAULT_PERIOD)) {
    return DEFAULT_PERIOD;
  }
  if (enabled.length === 0) return null;
  return enabled.reduce((longest, period) =>
    period.firstDate.getTime() < longest.firstDate.getTime() ? period : longest,
  ).period;
}

/** Each price as its return from the first price: the first point is 0% (R17). */
function totalReturns(points: readonly PricePoint[]): ChartPoint[] {
  if (points.length === 0) return [];
  const first = points[0].price;
  return points.map(({ date, price }) => ({
    date,
    value: price / first - 1,
  }));
}

/**
 * The `SP-DA` prices over the dates of the security's period: from the last
 * benchmark price on or before the security's first date (else the first
 * benchmark price after it) to the security's last date (R46).
 */
export function benchmarkPeriodPoints(
  benchmark: readonly PricePoint[],
  security: readonly PricePoint[],
): PricePoint[] {
  if (security.length === 0) return [];
  const first = security[0].date.getTime();
  const last = security[security.length - 1].date.getTime();
  let start = 0;
  while (
    start + 1 < benchmark.length &&
    benchmark[start + 1].date.getTime() <= first
  ) {
    start++;
  }
  return benchmark.slice(start).filter((point) => point.date.getTime() <= last);
}

const isUsable = (point: PricePoint) =>
  Number.isFinite(point.price) && point.price > 0;

/** The lines of the selected period, and how their values read. */
function chartLines(
  marketData: MarketDataQuery,
  symbol: string,
  period: ChartPeriod | undefined,
): Line[] {
  const data = marketData.data;
  const points = (period?.points ?? []).filter(isUsable);
  if (data?.variant === "bond") {
    // One price line, oldest date first, and no benchmark (R115, R118).
    return [
      {
        label: symbol,
        points: points.map(({ date, price }) => ({ date, value: price })),
      },
    ];
  }
  const benchmarkLine: Line = {
    label: BENCHMARK_NAME,
    points:
      data && data.benchmark.status === "ok"
        ? totalReturns(
            benchmarkPeriodPoints(
              (data.benchmarkPoints ?? []).filter(isUsable),
              points,
            ),
          )
        : DID_NOT_ANSWER,
  };
  return [{ label: symbol, points: totalReturns(points) }, benchmarkLine];
}

/**
 * Why the chart has no lines: the route or the prices call failed, or the
 * price history covers no period. Null when the chart has lines to draw.
 */
function chartFault(
  marketData: MarketDataQuery,
  periods: readonly ChartPeriod[] | null,
): string | null {
  const { data, isError } = marketData;
  if (isError || !data) return DID_NOT_ANSWER;
  const pricesFailed =
    data.variant === "bond"
      ? data.pricing.status !== "ok"
      : data.stats.status !== "ok";
  if (pricesFailed) return DID_NOT_ANSWER;
  // An answer with no `chartprices` is an empty value, not a failed call (R40).
  if (periods === null || periods.every((period) => period.disabled)) {
    return NOT_AVAILABLE;
  }
  return null;
}

/**
 * The data date of the chart (R38): the route's `dataDate`, the last chart
 * price date or, for a bond, the date of the newest bond data entry. Null
 * while loading, and when the route or its prices call failed.
 */
function chartDataDate(marketData: MarketDataQuery): string | null {
  const { data, isLoading, isError } = marketData;
  if (isLoading || isError || !data) return null;
  const pricesFailed =
    data.variant === "bond"
      ? data.pricing.status !== "ok"
      : data.stats.status !== "ok";
  return pricesFailed ? null : data.dataDate;
}

/** The periods of the market data, or null when the route sent none. */
const periodsOf = (marketData: MarketDataQuery): ChartPeriod[] | null =>
  marketData.data && !marketData.isError
    ? (marketData.data.periods ?? null)
    : null;

/**
 * The period the chart shows: the one the advisor selected while it is not
 * disabled, else the period the chart opens on (R52, R58). The risk and
 * drawdown panels show the same period (R21, R25). Null when no period is
 * enabled.
 */
export function activePeriod(
  marketData: MarketDataQuery,
  selected: ChartPeriodKey | null,
): ChartPeriodKey | null {
  const periods = periodsOf(marketData) ?? [];
  const isEnabled = periods.some(
    (period) => period.period === selected && !period.disabled,
  );
  return isEnabled ? selected : defaultPeriod(periods);
}

/**
 * The chart of the Overview tab with its six period buttons. A period whose
 * first date is before the first price is disabled (R56). The chart opens on
 * 1Y, or the longest period that is not disabled (R52, R58). The page keeps
 * the selection, so the risk and drawdown panels follow it (R21, R25).
 */
const SecurityChart = ({
  symbol,
  isBond,
  marketData,
  selected,
  onSelect,
}: {
  symbol: string;
  isBond: boolean;
  marketData: MarketDataQuery;
  selected: ChartPeriodKey | null;
  onSelect: (period: ChartPeriodKey) => void;
}) => {
  const { currentTheme } = useContext(ThemeContext);
  const periods = periodsOf(marketData);
  const byKey = new Map((periods ?? []).map((p) => [p.period, p]));
  const isEnabled = (key: ChartPeriodKey | null) =>
    key !== null && byKey.get(key)?.disabled === false;
  const active = activePeriod(marketData, selected);
  const fault = marketData.isLoading ? null : chartFault(marketData, periods);
  const lines =
    marketData.isLoading || fault !== null
      ? []
      : chartLines(marketData, symbol, active ? byKey.get(active) : undefined);
  // Each line keeps its legend color, also when the line before it is not drawn.
  const series: UserSerie<ChartPoint>[] = lines.flatMap((line, index) =>
    typeof line.points !== "string" && line.points.length > 0
      ? [{ label: line.label, data: line.points, color: COLORS[index] }]
      : [],
  );
  const format = isBond ? priceText : returnText;

  const primaryAxis = useMemo(
    (): AxisOptions<ChartPoint> => ({ getValue: (point) => point.date }),
    [],
  );
  const secondaryAxes = useMemo(
    (): AxisOptions<ChartPoint>[] => [
      {
        getValue: (point) => point.value,
        elementType: "line",
        formatters: {
          scale: (value: number | null) =>
            value === null ? "" : isBond ? priceText(value) : returnText(value),
        },
      } as AxisOptions<ChartPoint>,
    ],
    [isBond],
  );

  return (
    <section aria-label="Chart">
      <h3 className="fs-5 fw-semibold mb-1">
        {isBond ? "Price" : "Total return"}
      </h3>
      <DataDate date={chartDataDate(marketData)} />
      <ButtonGroup aria-label="Chart period" className="mt-2 mb-2">
        {PERIODS.map((key) => (
          <Button
            key={key}
            size="sm"
            variant={key === active ? "primary" : "outline-primary"}
            aria-pressed={key === active}
            disabled={!isEnabled(key)}
            onClick={() => onSelect(key)}
          >
            {key}
          </Button>
        ))}
      </ButtonGroup>
      {marketData.isLoading ? (
        <div className="text-muted">Loading FastTrack data…</div>
      ) : fault !== null ? (
        <div className="text-muted">{fault}</div>
      ) : (
        <>
          {series.length > 0 && (
            <div style={{ height: "320px" }}>
              <Chart
                options={{
                  data: series,
                  primaryAxis,
                  secondaryAxes,
                  defaultColors: COLORS,
                  dark: currentTheme === "dark",
                }}
              />
            </div>
          )}
          <ul aria-label="Chart lines" className="list-unstyled d-flex gap-4">
            {lines.map((line, index) => (
              <li key={line.label}>
                <span
                  className="d-inline-block rounded-circle align-middle me-2"
                  style={{
                    backgroundColor: COLORS[index],
                    width: "0.75rem",
                    height: "0.75rem",
                  }}
                />
                <span className="me-2">{line.label}</span>
                <span className="fw-semibold">
                  {typeof line.points === "string"
                    ? line.points
                    : line.points.length > 0
                      ? format(line.points[line.points.length - 1].value)
                      : NOT_AVAILABLE}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
};

export default SecurityChart;
